import React, { useState } from 'react';
import { FileText, Lightbulb, BookOpen, Users, Upload, Lock, X, FileUp, AlertTriangle, CheckCircle, Info, HelpCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import HelpButton from '../components/HelpButton';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const IEPHubPage = () => {
  const { isAuthenticated, user, demoMode } = useAuth();
  const [activeTab, setActiveTab] = useState('resources');
  const [loading, setLoading] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [improvedResult, setImprovedResult] = useState(null);
  const [formData, setFormData] = useState({
    child_description: '',
    grade_level: '',
    disability_category: '',
    current_challenges: [],
    area_of_need: '',
    strengths: '',
    current_supports: '',
    impact_area: ''
  });
  const [existingGoal, setExistingGoal] = useState('');
  const [goalContext, setGoalContext] = useState('');
  
  // Document upload state
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [documentText, setDocumentText] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  // Check if user is a real logged-in user (not demo)
  const isRealUser = isAuthenticated && !demoMode;
  
  // State resources
  const [selectedState, setSelectedState] = useState('');
  const [stateResources, setStateResources] = useState(null);
  const [stateResourcesLoading, setStateResourcesLoading] = useState(false);
  
  // IEP Topics
  const [selectedTopic, setSelectedTopic] = useState(null);

  const iepTopics = [
    {
      title: 'Understanding FAPE',
      description: 'Free Appropriate Public Education basics',
      content: {
        overview: 'FAPE (Free Appropriate Public Education) is a fundamental right under IDEA that ensures students with disabilities receive education services at no cost to families.',
        keyPoints: [
          'Education must be provided at public expense',
          'Must meet state educational standards',
          'Include appropriate grade levels of instruction',
          'Conform to the child\'s IEP'
        ],
        resources: [
          { title: 'IDEA FAPE Requirements', url: 'https://sites.ed.gov/idea/regs/b/a/300.17' },
          { title: 'Parent Guide to FAPE', url: 'https://www.parentcenterhub.org/fape/' }
        ]
      }
    },
    {
      title: 'Least Restrictive Environment (LRE)',
      description: 'Inclusion and placement principles',
      content: {
        overview: 'LRE requires that students with disabilities be educated with non-disabled peers to the maximum extent appropriate.',
        keyPoints: [
          'General education classroom is the first placement consideration',
          'Removal only when education cannot be achieved satisfactorily',
          'Supplementary aids and services must be considered',
          'Continuum of placement options must be available'
        ],
        resources: [
          { title: 'LRE Legal Requirements', url: 'https://sites.ed.gov/idea/regs/b/b/300.114' },
          { title: 'Inclusion Strategies', url: 'https://www.understood.org/articles/least-restrictive-environment-lre-what-you-need-to-know' }
        ]
      }
    },
    {
      title: 'SMART Goal Writing',
      description: 'Creating measurable IEP goals',
      content: {
        overview: 'SMART goals ensure IEP objectives are clear, trackable, and achievable within the specified timeframe.',
        keyPoints: [
          'Specific: Clearly defined target behavior',
          'Measurable: Can be quantified and tracked',
          'Achievable: Realistic for the student',
          'Relevant: Connected to educational needs',
          'Time-bound: Clear deadline for achievement'
        ],
        resources: [
          { title: 'SMART IEP Goals Guide', url: 'https://www.parentcenterhub.org/iep-goals/' },
          { title: 'Measurable Goals Examples', url: 'https://www.understood.org/articles/iep-goals-what-they-should-look-like' }
        ]
      }
    },
    {
      title: 'IEP Meeting Preparation',
      description: 'How to prepare for effective IEP meetings',
      content: {
        overview: 'Proper preparation ensures productive IEP meetings that result in appropriate services for your child.',
        keyPoints: [
          'Review current IEP and progress reports',
          'Prepare questions and concerns in advance',
          'Bring relevant data and documentation',
          'Know your rights and procedural safeguards'
        ],
        resources: [
          { title: 'Meeting Preparation Checklist', url: 'https://www.parentcenterhub.org/iep-meeting/' },
          { title: 'Questions to Ask at IEP Meetings', url: 'https://www.understood.org/articles/questions-to-ask-at-your-childs-iep-meeting' }
        ]
      }
    },
    {
      title: 'Accommodations vs. Modifications',
      description: 'Understanding the key differences',
      content: {
        overview: 'Accommodations change HOW a student learns, while modifications change WHAT a student learns.',
        keyPoints: [
          'Accommodations: Change in format, not content (extra time, assistive technology)',
          'Modifications: Change in curriculum or expectations (reduced assignments, alternative assessments)',
          'Accommodations don\'t lower standards; modifications may',
          'Both should be clearly specified in the IEP'
        ],
        resources: [
          { title: 'Accommodations Examples', url: 'https://www.understood.org/articles/accommodations-what-they-are-and-how-they-work' },
          { title: 'Modifications Guide', url: 'https://www.understood.org/articles/modifications-what-you-need-to-know' }
        ]
      }
    },
    {
      title: 'Transition Planning',
      description: 'Preparing for post-secondary life',
      content: {
        overview: 'Transition services help students with disabilities prepare for post-secondary education, employment, and independent living.',
        keyPoints: [
          'Must begin by age 16 (or younger if appropriate)',
          'Based on age-appropriate transition assessments',
          'Include postsecondary goals for education, employment, and independent living',
          'Coordinate with outside agencies when appropriate'
        ],
        resources: [
          { title: 'Transition Planning Guide', url: 'https://www.parentcenterhub.org/transition-planning/' },
          { title: 'Post-Secondary Options', url: 'https://www.understood.org/articles/transition-planning-and-ieps' }
        ]
      }
    }
  ];

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const tabs = [
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'goal-creator', label: 'Goal Creator', icon: Lightbulb },
    { id: 'goal-improver', label: 'Goal Improver', icon: FileText }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGenerateGoals = async () => {
    if (!formData.child_description.trim()) {
      alert('Please provide a child description to generate goals.');
      return;
    }

    setLoading(true);
    setGeneratedResult(null); // Clear previous results
    try {
      const response = await axios.post(`${API_URL}/api/iep/generate-goals`, formData, {
        timeout: 60000 // 60 second timeout
      });
      setGeneratedResult(response.data);
    } catch (error) {
      console.error('Error generating goals:', error);
      if (error.code === 'ECONNABORTED') {
        alert('The request timed out. Please try again or try with a simpler description.');
      } else if (error.response?.status === 500) {
        alert('The AI service is temporarily unavailable. Please try again in a moment.');
      } else {
        alert('Failed to generate IEP goals. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleImproveGoal = async () => {
    if (!existingGoal.trim()) {
      alert('Please enter a goal to improve.');
      return;
    }

    setLoading(true);
    setImprovedResult(null); // Clear previous results
    try {
      const response = await axios.post(`${API_URL}/api/iep/improve-goal`, {
        existing_goal: existingGoal,
        context: goalContext
      }, {
        timeout: 60000 // 60 second timeout
      });
      setImprovedResult(response.data);
    } catch (error) {
      console.error('Error improving goal:', error);
      if (error.code === 'ECONNABORTED') {
        alert('The request timed out. Please try again with a shorter goal or try later.');
      } else if (error.response?.status === 500) {
        alert('The AI service is temporarily unavailable. Please try again in a moment.');
      } else {
        alert('Failed to improve IEP goal. Please try again.');
      }
    }
    setLoading(false);
  };

  const loadStateResources = async () => {
    if (!selectedState) return;

    setStateResourcesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/iep/state-resources/${selectedState}`);
      setStateResources(response.data);
    } catch (error) {
      console.error('Error loading state resources:', error);
      alert('Failed to load state resources. Please try again.');
    }
    setStateResourcesLoading(false);
  };

  // SMART Rating Badge Component
  const SmartRatingBadge = ({ rating }) => {
    const colors = {
      'Met': 'bg-green-100 text-green-800 border-green-200',
      'Partially Met': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Not Met': 'bg-red-100 text-red-800 border-red-200',
      'Needs Review': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors[rating] || colors['Needs Review']}`}>
        {rating}
      </span>
    );
  };

  // Disclaimer Banner Component
  const DisclaimerBanner = ({ text }) => (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
      <div className="flex items-start">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <p className="ml-3 text-sm text-amber-800">
          <strong>Important:</strong> {text || "These are draft examples intended to help families prepare for IEP discussions. Final goals, services, and placements must be determined by the full IEP team in accordance with IDEA and district policy."}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="iep-hub-title">
            IEP Resource Hub
          </h1>
          <p className="text-gray-600 mt-2">
            Professional tools to help you prepare for your child's IEP discussions
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid={`iep-tab-${tab.id}`}
                >
                  <Icon size={20} className={`mr-2 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">State-Specific IEP Resources</h2>
              
              {/* State Selection */}
              <div className="mb-6">
                <label htmlFor="state-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select your state to see specific IEP requirements and resources
                </label>
                <div className="flex items-center space-x-4">
                  <select
                    id="state-select"
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose your state...</option>
                    {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  <button
                    onClick={loadStateResources}
                    disabled={!selectedState || stateResourcesLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center min-h-[44px]"
                  >
                    {stateResourcesLoading ? <LoadingSpinner size="small" text="" /> : 'Load Resources'}
                  </button>
                </div>
              </div>

              {/* State Resources Display */}
              {stateResources && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">
                      {stateResources.state_name} IEP Resources
                    </h3>
                    {stateResources.message ? (
                      <p className="text-blue-800">{stateResources.message}</p>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p><strong>Department of Education:</strong></p>
                          <a href={stateResources.dept_of_education} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            Visit State DOE →
                          </a>
                        </div>
                        <div>
                          <p><strong>Parent Information Center:</strong></p>
                          <a href={stateResources.parent_information_center} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            Get Parent Support →
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {stateResources.resources && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4">State-Specific Guides</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {stateResources.resources.map((resource, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <h5 className="font-medium text-gray-900 mb-2">{resource.title}</h5>
                            <p className="text-gray-600 text-sm mb-3">{resource.description}</p>
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Access Resource →
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {stateResources.key_laws && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Key State Laws</h4>
                      <ul className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                        {stateResources.key_laws.map((law, index) => (
                          <li key={index} className="text-sm text-gray-700">• {law}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Interactive IEP Topics */}
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Universal IEP Topics</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {iepTopics.map((topic, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedTopic(topic)}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left hover:bg-blue-100 hover:border-blue-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      data-testid={`iep-topic-${index}`}
                      aria-label={`Learn about ${topic.title}`}
                    >
                      <p className="text-blue-800 font-medium text-sm mb-1">{topic.title}</p>
                      <p className="text-blue-600 text-xs">{topic.description}</p>
                      <div className="mt-2 text-blue-500 text-xs font-medium">
                        Click to learn more →
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'goal-creator' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">IEP Goal Draft Creator</h2>
                <p className="text-gray-600 mt-1 text-sm">
                  Generate SMART goal drafts to discuss with your IEP team
                </p>
              </div>
              <div className="flex items-center text-blue-600 text-xs bg-blue-50 px-3 py-1.5 rounded-full">
                <Info size={14} className="mr-1.5" />
                For preparation only
              </div>
            </div>
            
            <DisclaimerBanner />
            
            <div className="space-y-6">
              {/* Required Fields */}
              <div>
                <label htmlFor="child-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Child Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="child-description"
                  name="child_description"
                  value={formData.child_description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your child's current situation, including age and any relevant context..."
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="grade-level" className="block text-sm font-medium text-gray-700 mb-2">
                    Grade Level
                  </label>
                  <select
                    id="grade-level"
                    name="grade_level"
                    value={formData.grade_level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select grade level</option>
                    <option value="preschool">Preschool</option>
                    <option value="kindergarten">Kindergarten</option>
                    <option value="1st">1st Grade</option>
                    <option value="2nd">2nd Grade</option>
                    <option value="3rd">3rd Grade</option>
                    <option value="4th">4th Grade</option>
                    <option value="5th">5th Grade</option>
                    <option value="middle">Middle School</option>
                    <option value="high">High School</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="disability-category" className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Disability Category
                  </label>
                  <select
                    id="disability-category"
                    name="disability_category"
                    value={formData.disability_category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select category</option>
                    <option value="autism">Autism Spectrum Disorder</option>
                    <option value="intellectual">Intellectual Disability</option>
                    <option value="learning">Specific Learning Disability</option>
                    <option value="adhd">ADHD</option>
                    <option value="emotional">Emotional/Behavioral</option>
                    <option value="physical">Physical Disability</option>
                    <option value="hearing">Hearing Impairment</option>
                    <option value="visual">Visual Impairment</option>
                    <option value="speech">Speech/Language Impairment</option>
                    <option value="multiple">Multiple Disabilities</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Additional Context Fields */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <p className="text-sm font-medium text-gray-700 flex items-center">
                  <HelpCircle size={16} className="mr-2 text-gray-400" />
                  Additional context helps create more tailored goal drafts:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="area-of-need" className="block text-sm font-medium text-gray-600 mb-1">
                      Primary Area of Need
                    </label>
                    <select
                      id="area-of-need"
                      name="area_of_need"
                      value={formData.area_of_need}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select area</option>
                      <option value="communication">Communication</option>
                      <option value="reading">Reading/Literacy</option>
                      <option value="math">Math</option>
                      <option value="writing">Written Expression</option>
                      <option value="behavior">Behavior/Self-Regulation</option>
                      <option value="social">Social Skills</option>
                      <option value="executive">Executive Functioning</option>
                      <option value="motor">Motor Skills</option>
                      <option value="adaptive">Adaptive/Life Skills</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="impact-area" className="block text-sm font-medium text-gray-600 mb-1">
                      How does this impact the student?
                    </label>
                    <select
                      id="impact-area"
                      name="impact_area"
                      value={formData.impact_area}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select impact</option>
                      <option value="access">Access to curriculum</option>
                      <option value="participation">Participation in activities</option>
                      <option value="progress">Academic progress</option>
                      <option value="all">All of the above</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="strengths" className="block text-sm font-medium text-gray-600 mb-1">
                    Student Strengths
                  </label>
                  <input
                    type="text"
                    id="strengths"
                    name="strengths"
                    value={formData.strengths}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g., visual learner, good memory, motivated by praise..."
                  />
                </div>
                
                <div>
                  <label htmlFor="current-supports" className="block text-sm font-medium text-gray-600 mb-1">
                    Current Supports Already in Place
                  </label>
                  <input
                    type="text"
                    id="current-supports"
                    name="current_supports"
                    value={formData.current_supports}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="e.g., speech therapy, aide support, preferential seating..."
                  />
                </div>
              </div>
              
              <button 
                onClick={handleGenerateGoals}
                disabled={loading || !formData.child_description.trim()}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center min-h-[44px]"
              >
                {loading ? <LoadingSpinner size="small" text="" /> : 'Generate Draft Goals'}
              </button>
            </div>

            {/* Generated Goals Results */}
            {generatedResult && (
              <div className="mt-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Draft Goals for Team Discussion</h3>
                  {generatedResult.ai_generated && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">AI-Assisted</span>
                  )}
                </div>
                
                <DisclaimerBanner text={generatedResult.disclaimer} />

                {/* Missing Information Note */}
                {generatedResult.missing_information_note && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                      <p className="ml-3 text-sm text-blue-800">{generatedResult.missing_information_note}</p>
                    </div>
                  </div>
                )}
                
                {(generatedResult.goals || []).map((goal, index) => (
                  <div key={goal.id || index} className="bg-white border-2 border-green-200 rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-6 py-3 border-b border-green-200">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-medium text-green-800">
                          Goal {index + 1}: {goal.category}
                        </h4>
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          {goal.timeline}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6 space-y-4">
                      {/* Present Level Connection */}
                      {goal.present_level_connection && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Present Level Connection</h5>
                          <p className="text-sm text-gray-600 italic">{goal.present_level_connection}</p>
                        </div>
                      )}
                      
                      {/* Goal Statement */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">SMART Goal Statement</h5>
                        <p className="text-gray-800">{goal.goal_statement || goal.goal_text}</p>
                      </div>
                      
                      {/* Measurement Details */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Measurement Method</h5>
                          <p className="text-sm text-gray-700">{goal.measurement_method || goal.measurable_criteria}</p>
                        </div>
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mastery Criteria</h5>
                          <p className="text-sm text-gray-700">{goal.mastery_criteria || goal.measurable_criteria}</p>
                        </div>
                      </div>
                      
                      {/* Support Needed */}
                      {goal.support_needed && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Supports & Services Needed</h5>
                          <p className="text-sm text-gray-700">{goal.support_needed}</p>
                        </div>
                      )}
                      
                      {/* Notes for Team */}
                      {goal.notes_for_team && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <h5 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1 flex items-center">
                            <Users size={14} className="mr-1.5" />
                            Notes for the IEP Team
                          </h5>
                          <p className="text-sm text-amber-800">{goal.notes_for_team}</p>
                        </div>
                      )}
                      
                      {/* Assumptions Made */}
                      {goal.assumptions_made && (
                        <div className="text-xs text-gray-500 bg-gray-100 rounded px-3 py-2">
                          <strong>Assumptions:</strong> {goal.assumptions_made}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* General Recommendations */}
                {generatedResult.general_recommendations && generatedResult.general_recommendations.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h5 className="font-medium text-purple-800 mb-3">General Recommendations</h5>
                    <ul className="text-sm text-purple-700 space-y-1">
                      {generatedResult.general_recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle size={14} className="mr-2 mt-0.5 text-purple-500" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'goal-improver' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">IEP Goal SMART Analyzer</h2>
                <p className="text-gray-600 mt-1 text-sm">
                  Analyze your existing goal against SMART criteria and get improvement suggestions
                </p>
              </div>
              <div className="flex items-center text-blue-600 text-xs bg-blue-50 px-3 py-1.5 rounded-full">
                <Info size={14} className="mr-1.5" />
                For preparation only
              </div>
            </div>
            
            <DisclaimerBanner />
            
            <div className="space-y-6">
              {/* Document Upload Section */}
              {isRealUser ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-blue-900 flex items-center">
                      <Upload size={18} className="mr-2" />
                      Upload IEP Document (Optional)
                    </h3>
                    {uploadedDocument && (
                      <button
                        onClick={() => {
                          setUploadedDocument(null);
                          setDocumentText('');
                          setUploadError('');
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <X size={16} className="mr-1" /> Clear
                      </button>
                    )}
                  </div>
                  
                  {!uploadedDocument ? (
                    <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        id="iep-document"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setUploadLoading(true);
                            setUploadError('');
                            setUploadedDocument(file);
                            
                            if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                const text = event.target.result;
                                setDocumentText(text);
                                if (text.toLowerCase().includes('goal')) {
                                  const goalSection = text.split(/goal/i)[1]?.substring(0, 500) || '';
                                  if (goalSection) {
                                    setExistingGoal(goalSection.trim());
                                  }
                                }
                                setUploadLoading(false);
                              };
                              reader.onerror = () => {
                                setUploadError('Error reading file');
                                setUploadLoading(false);
                              };
                              reader.readAsText(file);
                            } else {
                              setUploadLoading(false);
                              setDocumentText('Document uploaded. Please copy and paste the relevant goal text below.');
                            }
                          }
                        }}
                        className="hidden"
                      />
                      <label htmlFor="iep-document" className="cursor-pointer">
                        <FileUp size={32} className="mx-auto text-blue-400 mb-2" />
                        <p className="text-blue-700 font-medium">Click to upload IEP document</p>
                        <p className="text-sm text-blue-500 mt-1">Supports PDF, DOC, DOCX, TXT files</p>
                      </label>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center">
                        <FileText size={24} className="text-blue-600 mr-3" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{uploadedDocument.name}</p>
                          <p className="text-sm text-gray-500">{(uploadedDocument.size / 1024).toFixed(1)} KB</p>
                        </div>
                        {uploadLoading && <LoadingSpinner size="small" text="" />}
                      </div>
                      {documentText && !uploadLoading && (
                        <p className="text-sm text-green-600 mt-2">✓ Document loaded</p>
                      )}
                    </div>
                  )}
                  
                  {uploadError && <p className="text-sm text-red-600 mt-2">{uploadError}</p>}
                  <p className="text-xs text-blue-600 mt-2">🔒 Your document is processed securely and never stored.</p>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center text-gray-600">
                    <Lock size={18} className="mr-2" />
                    <span className="text-sm">
                      <strong>Document upload available for registered users.</strong> Create a free account to upload your IEP document.
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="existing-goal" className="block text-sm font-medium text-gray-700 mb-2">
                  Existing IEP Goal <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="existing-goal"
                  value={existingGoal}
                  onChange={(e) => setExistingGoal(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Paste your current IEP goal here for SMART analysis..."
                />
              </div>
              
              <div>
                <label htmlFor="goal-context" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Context (Optional)
                </label>
                <input
                  type="text"
                  id="goal-context"
                  value={goalContext}
                  onChange={(e) => setGoalContext(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional context about the student or goal area..."
                />
              </div>
              
              <button 
                onClick={handleImproveGoal}
                disabled={loading || !existingGoal.trim()}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center min-h-[44px]"
              >
                {loading ? <LoadingSpinner size="small" text="" /> : 'Analyze Goal with SMART Criteria'}
              </button>
            </div>
            
            {/* Improved Goal Results */}
            {improvedResult && (
              <div className="mt-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">SMART Analysis Results</h3>
                  {improvedResult.ai_generated && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">AI-Assisted</span>
                  )}
                </div>
                
                <DisclaimerBanner text={improvedResult.disclaimer} />

                {/* Original Goal */}
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">Original Goal</h4>
                  <p className="text-gray-700 italic">{improvedResult.original_goal}</p>
                </div>

                {/* SMART Analysis */}
                {improvedResult.smart_analysis && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                      <h4 className="font-medium text-blue-800">SMART Criteria Analysis</h4>
                    </div>
                    <div className="p-4 space-y-3">
                      {Object.entries(improvedResult.smart_analysis).map(([key, value]) => (
                        <div key={key} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 capitalize">{key}</span>
                            <p className="text-sm text-gray-600 mt-0.5">{value.notes}</p>
                          </div>
                          <SmartRatingBadge rating={value.rating} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Improved Goal */}
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5">
                  <h4 className="font-semibold text-green-800 mb-3">Suggested Improved Goal</h4>
                  {typeof improvedResult.improved_goal === 'object' ? (
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <p className="text-gray-800">{improvedResult.improved_goal.goal_statement}</p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div><strong className="text-green-700">Condition:</strong> <span className="text-gray-700">{improvedResult.improved_goal.condition}</span></div>
                        <div><strong className="text-green-700">Behavior:</strong> <span className="text-gray-700">{improvedResult.improved_goal.behavior}</span></div>
                        <div><strong className="text-green-700">Measurement:</strong> <span className="text-gray-700">{improvedResult.improved_goal.measurement}</span></div>
                        <div><strong className="text-green-700">Criterion:</strong> <span className="text-gray-700">{improvedResult.improved_goal.criterion}</span></div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-800">{improvedResult.improved_goal}</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Improvements Made */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 mb-3 flex items-center">
                      <CheckCircle size={16} className="mr-2" />
                      Improvements Made
                    </h5>
                    <ul className="text-sm text-blue-700 space-y-1.5">
                      {(improvedResult.improvements_made || []).map((improvement, i) => (
                        <li key={i}>• {improvement}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Questions for Team */}
                  {improvedResult.questions_for_team && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h5 className="font-medium text-amber-800 mb-3 flex items-center">
                        <Users size={16} className="mr-2" />
                        Questions for the IEP Team
                      </h5>
                      <ul className="text-sm text-amber-700 space-y-1.5">
                        {improvedResult.questions_for_team.map((question, i) => (
                          <li key={i}>• {question}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Additional Suggestions */}
                {improvedResult.suggestions && improvedResult.suggestions.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h5 className="font-medium text-purple-800 mb-3">Additional Suggestions</h5>
                    <ul className="text-sm text-purple-700 space-y-1">
                      {improvedResult.suggestions.map((suggestion, i) => (
                        <li key={i}>• {suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cautions */}
                {improvedResult.cautions && (
                  <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                    <p className="text-sm text-gray-600">
                      <strong>Note:</strong> {improvedResult.cautions}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* IEP Topic Detail Modal */}
        {selectedTopic && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{selectedTopic.title}</h2>
                <button
                  onClick={() => setSelectedTopic(null)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Overview</h3>
                  <p className="text-gray-700">{selectedTopic.content.overview}</p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Key Points</h3>
                  <ul className="space-y-2">
                    {selectedTopic.content.keyPoints.map((point, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle size={18} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Resources</h3>
                  <div className="space-y-3">
                    {selectedTopic.content.resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-blue-50 border border-blue-200 rounded-lg p-4 hover:bg-blue-100 transition-colors"
                      >
                        <span className="text-blue-700 font-medium">{resource.title} →</span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setSelectedTopic(null)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <HelpButton pageName="iep" />
      </div>
    </div>
  );
};

export default IEPHubPage;
